import { useMemo } from 'react'
import type { MonitorEventItem } from '@/lib/api'

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  info: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
}

const EVENT_ICON: Record<string, string> = {
  ransomware: '🔒',
  vulnerability: '⚠️',
  cyber_attack: '⚡',
  conflict: '💥',
  explosion: '💥',
  protest: '✊',
  disaster: '🔥',
  economic: '📉',
}

interface Props {
  events: MonitorEventItem[]
  onEventClick: (event: MonitorEventItem) => void
}

export default function AttackTicker({ events, onEventClick }: Props) {
  const recent = useMemo(() => {
    const seen = new Set<string>()
    const deduped: MonitorEventItem[] = []
    for (const ev of events) {
      const key = ev.title.slice(0, 45).toLowerCase().replace(/\s+/g, ' ').trim()
      if (!seen.has(key)) {
        seen.add(key)
        deduped.push(ev)
      }
      if (deduped.length >= 40) break
    }
    return deduped
  }, [events])

  if (recent.length === 0) return null

  const doubled = [...recent, ...recent]

  return (
    <div className="w-full bg-gray-950 border-t border-green-900/40 overflow-hidden">
      <div className="flex items-center h-8">
        <div className="flex-shrink-0 px-3 py-1 border-r border-green-900/40 flex items-center gap-1.5 z-10 bg-gray-950">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shadow-[0_0_6px_rgba(239,68,68,0.6)]" />
          <span className="text-[9px] font-bold text-red-400 uppercase tracking-[0.15em] whitespace-nowrap font-mono">
            LIVE FEED
          </span>
        </div>

        <div className="flex-1 overflow-hidden relative">
          <div
            className="flex items-center gap-0 animate-ticker"
            style={{
              width: 'max-content',
              animation: `tickerScroll ${Math.max(recent.length * 3, 30)}s linear infinite`,
            }}
          >
            {doubled.map((ev, i) => (
              <button
                key={`${ev.id}-${i}`}
                onClick={() => onEventClick(ev)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-0.5 hover:bg-green-500/10 transition-colors border-r border-gray-800/20 group"
              >
                <span className="text-xs">{EVENT_ICON[ev.event_type] || '📡'}</span>
                <span className={`text-[8px] font-mono px-1 py-0.5 rounded border ${SEVERITY_BADGE[ev.severity]}`}>
                  {ev.severity.slice(0, 4).toUpperCase()}
                </span>
                <span className="text-[10px] text-gray-400 group-hover:text-gray-200 transition-colors truncate max-w-[200px] font-mono">
                  {ev.title.length > 50 ? ev.title.slice(0, 50) + '…' : ev.title}
                </span>
                {ev.country_code && (
                  <span className="text-[8px] text-gray-700 font-mono">{ev.country_code}</span>
                )}
                <span className="text-[8px] text-gray-700 font-mono">{getTimeAgo(new Date(ev.timestamp))}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  )
}

function getTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}
